// Homepage BTC candlestick chart with Chronos2 OHLC forecast bars.
(function () {
    'use strict';

    const VIEWS = {
        '1H': {
            key: '1H', interval: 'HOUR_1', limit: 72, steps: 12,
            horizon: 1, horizonLabel: '+1h', title: 'Hourly Forecast', barMs: 3600000,
        },
        '7H': {
            key: '7H', interval: 'HOUR_1', limit: 168, steps: 24,
            horizon: 7, horizonLabel: '+7h', title: 'Hourly Forecast', barMs: 3600000,
        },
        '7D': {
            key: '7D', interval: 'DAY_1', limit: 90, steps: 7,
            horizon: 7, horizonLabel: '+7d', title: 'Daily Forecast', barMs: 86400000,
        },
    };
    const DEFAULT_VIEW = '1H';
    const CACHE_TTL = 5 * 60 * 1000;
    const COLORS = {
        up: '#35cd8b',
        down: '#ff6b5f',
        wick: 'rgba(226, 232, 240, 0.66)',
        forecastUp: '#7dd3fc',
        forecastDown: '#a5b4fc',
        bandFill: 'rgba(125, 211, 252, 0.12)',
        bandFillOuter: 'rgba(125, 211, 252, 0.065)',
        axis: '#9fb0c4',
        grid: 'rgba(148, 163, 184, 0.12)',
        forecastPanel: 'rgba(125, 211, 252, 0.035)',
        text: '#e5edf8',
        labelBg: 'rgba(9,17,30,0.86)',
        gold: '#f5bf3d',
        past: '#7aa2ff',
        pastLine: 'rgba(122, 162, 255, 0.85)',
        pastFill: 'rgba(122, 162, 255, 0.13)',
        pastMarker: 'rgba(122, 162, 255, 0.55)',
    };

    const state = {
        view: VIEWS[DEFAULT_VIEW],
        cache: {},
        historyCache: {},
        history: null,
        hoverIndex: -1,
        pinnedIndex: -1,
        token: 0,
        historyToken: 0,
        chart: null,
        series: null,
        data: null,
        errorTimer: null,
        started: false,
        redrawQueued: false,
    };

    function requestRedraw() {
        if (state.redrawQueued || !state.chart || !state.series) return;
        state.redrawQueued = true;
        const run = () => {
            state.redrawQueued = false;
            if (state.chart && state.series) drawChart(state.chart.canvas, state.chart.tooltip, state.series);
        };
        if (window.requestAnimationFrame) window.requestAnimationFrame(run);
        else setTimeout(run, 16);
    }

    function normalizeTs(ts) {
        return ts && ts < 1e12 ? ts * 1000 : ts;
    }

    function formatPrice(value) {
        if (!Number.isFinite(value)) return '-';
        return '$' + Math.round(value).toLocaleString();
    }

    function formatTime(ts, view) {
        const d = new Date(ts);
        if (view && view.barMs >= 86400000) {
            return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
        }
        return d.toLocaleString(undefined, {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    }

    function formatAxis(ts, view) {
        const d = new Date(ts);
        if (view && view.barMs >= 86400000) {
            return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
        }
        if (view && view.limit <= 96) {
            return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
        }
        return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    }

    function candleColor(candle, forecast) {
        if (candle.close >= candle.open) return forecast ? COLORS.forecastUp : COLORS.up;
        return forecast ? COLORS.forecastDown : COLORS.down;
    }

    function createCanvas(container) {
        container.innerHTML = [
            '<div class="btc-chart-canvas-wrap">',
            '<canvas class="btc-chart-canvas" aria-label="BTC USDT forecast candlestick chart"></canvas>',
            '<div class="btc-chart-tooltip" hidden></div>',
            '</div>',
        ].join('');
        return {
            container: container,
            canvas: container.querySelector('canvas'),
            tooltip: container.querySelector('.btc-chart-tooltip'),
        };
    }

    function buildSeries(candles, forecast, view) {
        const history = candles
            .filter(c => c && c.timestamp > 0 && validCandle(c))
            .map(c => ({
                timestamp: normalizeTs(c.timestamp),
                open: Number(c.open),
                high: Number(c.high),
                low: Number(c.low),
                close: Number(c.close),
                forecast: false,
            }))
            .sort((a, b) => a.timestamp - b.timestamp)
            .slice(-view.limit);
        const predicted = forecast
            .filter(c => c && c.timestamp > 0 && validCandle(c))
            .map(c => ({
                timestamp: normalizeTs(c.timestamp),
                open: Number(c.open),
                high: Number(c.high),
                low: Number(c.low),
                close: Number(c.close),
                lowBand: Number(c.low_band || c.low),
                highBand: Number(c.high_band || c.high),
                forecast: true,
                engine: c.engine || '',
            }))
            .sort((a, b) => a.timestamp - b.timestamp)
            .slice(0, view.steps);
        return { history, predicted, all: history.concat(predicted), view: view };
    }

    function validCandle(candle) {
        return ['open', 'high', 'low', 'close'].every(key => Number.isFinite(Number(candle[key])));
    }

    function drawChart(canvas, tooltip, series) {
        const view = series.view || VIEWS[DEFAULT_VIEW];
        const ctx = canvas.getContext('2d');
        if (typeof ctx.setLineDash !== 'function') ctx.setLineDash = function () {};
        if (!ctx.__safeRoundRect) {
            // Early Chromium builds (e.g. Honor Android) reject array radii; fall back to a number, then a plain rect.
            const native = typeof ctx.roundRect === 'function' ? ctx.roundRect : null;
            ctx.roundRect = function (x, y, w, h, r) {
                const radius = Array.isArray(r) ? r[0] : r;
                if (native) {
                    try { return native.call(this, x, y, w, h, r); } catch (_) {}
                    try { return native.call(this, x, y, w, h, radius || 0); } catch (_) {}
                }
                this.rect(x, y, w, h);
            };
            ctx.__safeRoundRect = true;
        }
        const rect = canvas.getBoundingClientRect();
        const width = Math.max(320, Math.floor(rect.width));
        const height = Math.max(260, Math.floor(rect.height));
        const dpr = window.devicePixelRatio || 1;
        canvas.width = Math.floor(width * dpr);
        canvas.height = Math.floor(height * dpr);
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, width, height);

        const s = Math.min(1.8, Math.max(1, height / 300));
        const pad = { top: Math.round(14 * s), right: Math.round(74 * s), bottom: Math.round(34 * s), left: 10 };
        const plotW = width - pad.left - pad.right;
        const plotH = height - pad.top - pad.bottom;
        const candleValues = [];
        series.all.forEach(c => candleValues.push(c.high, c.low));
        const candleMin = Math.min.apply(null, candleValues);
        const candleMax = Math.max.apply(null, candleValues);
        // Far-out confidence bands can be several times the candle range; let them
        // extend the axis a little, not flatten the price action.
        const slack = Math.max(1, candleMax - candleMin) * 0.45;
        let minY = candleMin;
        let maxY = candleMax;
        series.predicted.forEach(c => {
            if (Number.isFinite(c.lowBand)) minY = Math.min(minY, Math.max(c.lowBand, candleMin - slack));
            if (Number.isFinite(c.highBand)) maxY = Math.max(maxY, Math.min(c.highBand, candleMax + slack));
        });
        const span = Math.max(1, maxY - minY);
        const lo = minY - span * 0.05;
        const hi = maxY + span * 0.05;
        const count = series.all.length;
        const step = count > 0 ? plotW / count : plotW;
        const bodyW = Math.max(1.5, Math.min(7 * s, step * 0.62));

        function xAt(index) {
            return pad.left + index * step + step / 2;
        }

        function yAt(value) {
            return pad.top + (hi - value) / (hi - lo) * plotH;
        }

        ctx.fillStyle = 'rgba(8, 17, 31, 0)';
        ctx.fillRect(0, 0, width, height);

        ctx.strokeStyle = COLORS.grid;
        ctx.lineWidth = 1;
        ctx.font = Math.round(12 * s) + 'px system-ui, sans-serif';
        ctx.fillStyle = COLORS.axis;
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        for (let i = 0; i <= 4; i++) {
            const y = pad.top + (plotH / 4) * i;
            const value = hi - ((hi - lo) / 4) * i;
            ctx.beginPath();
            ctx.moveTo(pad.left, y);
            ctx.lineTo(width - pad.right + 6, y);
            ctx.stroke();
            ctx.fillText(formatPrice(value), width - pad.right + 12, y);
        }

        const targetTicks = Math.max(3, Math.min(8, Math.floor(plotW / (78 * s))));
        const stride = Math.max(1, Math.round(count / targetTicks));
        for (let index = count - 1; index >= 0; index -= stride) {
            const c = series.all[index];
            const x = xAt(index);
            ctx.strokeStyle = 'rgba(148, 163, 184, 0.1)';
            ctx.setLineDash([3, 5]);
            ctx.beginPath();
            ctx.moveTo(x, pad.top);
            ctx.lineTo(x, pad.top + plotH);
            ctx.stroke();
            ctx.setLineDash([]);
            ctx.fillStyle = COLORS.axis;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'top';
            const label = formatAxis(c.timestamp, view);
            if (x - ctx.measureText(label).width / 2 >= pad.left) {
                ctx.fillText(label, x, pad.top + plotH + 12);
            }
        }

        if (series.predicted.length) {
            const start = series.history.length;
            const panelX = pad.left + start * step;
            ctx.fillStyle = COLORS.forecastPanel;
            ctx.fillRect(panelX, pad.top, width - pad.right - panelX, plotH);

            // Bands may be clipped by the axis range above; keep them inside the plot.
            ctx.save();
            ctx.beginPath();
            ctx.rect(pad.left, pad.top, plotW, plotH);
            ctx.clip();

            ctx.beginPath();
            series.predicted.forEach((c, i) => {
                const x = xAt(start + i);
                const y = yAt(c.highBand + (hi - lo) * 0.04);
                if (i === 0) ctx.moveTo(x, y);
                else ctx.lineTo(x, y);
            });
            for (let i = series.predicted.length - 1; i >= 0; i--) {
                const c = series.predicted[i];
                ctx.lineTo(xAt(start + i), yAt(c.lowBand - (hi - lo) * 0.04));
            }
            ctx.closePath();
            ctx.fillStyle = COLORS.bandFillOuter;
            ctx.fill();

            ctx.beginPath();
            series.predicted.forEach((c, i) => {
                const x = xAt(start + i);
                const y = yAt(c.highBand);
                if (i === 0) ctx.moveTo(x, y);
                else ctx.lineTo(x, y);
            });
            for (let i = series.predicted.length - 1; i >= 0; i--) {
                const c = series.predicted[i];
                ctx.lineTo(xAt(start + i), yAt(c.lowBand));
            }
            ctx.closePath();
            ctx.fillStyle = COLORS.bandFill;
            ctx.fill();
            ctx.restore();

            const boundaryX = pad.left + start * step;
            ctx.strokeStyle = 'rgba(125, 211, 252, 0.25)';
            ctx.setLineDash([4, 6]);
            ctx.beginPath();
            ctx.moveTo(boundaryX, pad.top);
            ctx.lineTo(boundaryX, pad.top + plotH);
            ctx.stroke();
            ctx.setLineDash([]);
        }

        series.all.forEach((c, index) => {
            const x = xAt(index);
            const color = candleColor(c, c.forecast);
            const top = yAt(Math.max(c.open, c.close));
            const bottom = yAt(Math.min(c.open, c.close));
            const high = yAt(c.high);
            const low = yAt(c.low);
            ctx.strokeStyle = c.forecast ? color : COLORS.wick;
            ctx.fillStyle = color;
            ctx.globalAlpha = c.forecast ? 0.95 : 0.82;
            ctx.lineWidth = c.forecast ? 1.2 : 0.9;
            ctx.beginPath();
            ctx.moveTo(x, high);
            ctx.lineTo(x, low);
            ctx.stroke();
            ctx.fillRect(x - bodyW / 2, top, bodyW, Math.max(2, bottom - top));
            ctx.globalAlpha = 1;
        });

        const geo = { xAt: xAt, yAt: yAt, pad: pad, plotH: plotH, plotW: plotW, s: s, width: width, count: count };
        const active = activeSnapshot(series);
        drawSnapshotMarkers(ctx, series, geo);
        if (active) drawSnapshotOverlay(ctx, series, active, geo);

        drawHorizonMarker(ctx, series, view, geo);

        const latest = series.history[series.history.length - 1] || series.all[series.all.length - 1];
        if (latest) {
            const y = yAt(latest.close);
            const label = formatPrice(latest.close);
            ctx.font = '700 ' + Math.round(11 * s) + 'px system-ui, sans-serif';
            const labelW = Math.max(58 * s, ctx.measureText(label).width + 16);
            const labelX = width - pad.right + 8;
            ctx.strokeStyle = 'rgba(125, 211, 252, 0.34)';
            ctx.setLineDash([3, 5]);
            ctx.beginPath();
            ctx.moveTo(pad.left, y);
            ctx.lineTo(width - pad.right + 6, y);
            ctx.stroke();
            ctx.setLineDash([]);
            ctx.fillStyle = COLORS.gold;
            ctx.beginPath();
            ctx.roundRect(labelX, y - 11 * s, labelW, 22 * s, [5]);
            ctx.fill();
            ctx.fillStyle = '#111827';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(label, labelX + labelW / 2, y);
        }

        const badge = 'BTC/USDT · ' + view.key;
        ctx.font = '600 ' + Math.round(12 * s) + 'px system-ui, sans-serif';
        const badgeW = Math.round(ctx.measureText(badge).width + 24 * s);
        ctx.fillStyle = COLORS.labelBg;
        ctx.strokeStyle = 'rgba(125,211,252,0.14)';
        ctx.beginPath();
        ctx.roundRect(12, 8, badgeW, Math.round(30 * s), [7]);
        ctx.fill();
        ctx.stroke();
        ctx.fillStyle = COLORS.text;
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        ctx.fillText(badge, 12 + Math.round(12 * s), 8 + 15 * s);

        const hit = series.all.map((c, index) => ({ x: xAt(index), index: index, candle: c }));

        function nearestPoint(event) {
            const bounds = canvas.getBoundingClientRect();
            const px = event.clientX - bounds.left;
            let nearest = hit[0];
            if (!nearest) return null;
            for (const point of hit) {
                if (Math.abs(point.x - px) < Math.abs(nearest.x - px)) nearest = point;
            }
            return nearest;
        }

        function showTooltip(point) {
            const c = point.candle;
            const snapshot = snapshotAt(point.index, series);
            tooltip.hidden = false;
            tooltip.classList.toggle('has-forecast', !!snapshot);
            const tipWidth = snapshot ? 268 : 210;
            // A replayed forecast is drawn to the right of the hovered bar, so
            // put the card on the left when there is room for it.
            const left = snapshot && point.x - tipWidth - 14 > 8 ? point.x - tipWidth - 14 : point.x + 12;
            tooltip.style.left = Math.min(Math.max(8, width - tipWidth - 8), Math.max(8, left)) + 'px';
            let html =
                '<strong>' + (c.forecast ? 'Forecast' : 'Historical') + '</strong><br>' +
                formatTime(c.timestamp, view) + '<br>' +
                'O ' + formatPrice(c.open) + ' H ' + formatPrice(c.high) + '<br>' +
                'L ' + formatPrice(c.low) + ' C ' + formatPrice(c.close);
            if (snapshot) html += snapshotTooltipHTML(snapshot, view);
            tooltip.innerHTML = html;
            // The wrapper clips overflow, so keep the whole card on the canvas.
            const tipHeight = tooltip.offsetHeight || 120;
            tooltip.style.top = Math.max(8, Math.min(height - tipHeight - 8, yAt(c.high) - 18)) + 'px';
        }

        canvas.onmousemove = event => {
            const point = nearestPoint(event);
            if (!point) return;
            const wasIndex = state.hoverIndex;
            state.hoverIndex = point.index;
            showTooltip(point);
            if (wasIndex !== point.index && state.pinnedIndex < 0) requestRedraw();
        };
        canvas.onmouseleave = () => {
            tooltip.hidden = true;
            const had = state.hoverIndex;
            state.hoverIndex = -1;
            if (had >= 0 && state.pinnedIndex < 0) requestRedraw();
        };
        canvas.onclick = event => {
            const point = nearestPoint(event);
            if (!point) return;
            const snapshot = snapshotAt(point.index, series);
            if (!snapshot && state.pinnedIndex < 0) return;
            state.pinnedIndex = state.pinnedIndex === point.index ? -1 : (snapshot ? point.index : -1);
            requestRedraw();
        };
        canvas.style.cursor = 'crosshair';
    }

    // ===== past forecasts (hover replay) =====

    function snapshotAt(index, series) {
        if (!state.history || index < 0 || index >= series.history.length) return null;
        const candle = series.all[index];
        if (!candle || candle.forecast) return null;
        return state.history.byTs[candle.timestamp] || null;
    }

    function activeSnapshot(series) {
        const index = state.pinnedIndex >= 0 ? state.pinnedIndex : state.hoverIndex;
        const snapshot = snapshotAt(index, series);
        return snapshot ? { index: index, snapshot: snapshot } : null;
    }

    // Small ticks under bars that have a stored forecast, so it is discoverable
    // that those points are hoverable.
    function drawSnapshotMarkers(ctx, series, geo) {
        if (!state.history || !state.history.count) return;
        const y = geo.pad.top + geo.plotH - 3;
        const radius = Math.max(1.1, 1.5 * geo.s);
        ctx.fillStyle = COLORS.pastMarker;
        for (let index = 0; index < series.history.length; index++) {
            if (!snapshotAt(index, series)) continue;
            ctx.beginPath();
            ctx.arc(geo.xAt(index), y, radius, 0, Math.PI * 2);
            ctx.fill();
        }
    }

    function drawSnapshotOverlay(ctx, series, active, geo) {
        const steps = active.snapshot.steps || [];
        if (!steps.length) return;
        const points = [];
        for (let i = 0; i < steps.length; i++) {
            const index = active.index + 1 + i;
            if (index >= geo.count) break;
            const step = steps[i];
            if (!Number.isFinite(Number(step.c))) continue;
            points.push({ x: geo.xAt(index), step: step });
        }
        if (!points.length) return;

        ctx.save();
        ctx.beginPath();
        ctx.rect(geo.pad.left, geo.pad.top, geo.plotW, geo.plotH);
        ctx.clip();

        const hasBand = points.every(p => Number(p.step.lb) > 0 && Number(p.step.hb) > 0);
        if (hasBand) {
            ctx.beginPath();
            points.forEach((p, i) => {
                const y = geo.yAt(Number(p.step.hb));
                if (i === 0) ctx.moveTo(p.x, y);
                else ctx.lineTo(p.x, y);
            });
            for (let i = points.length - 1; i >= 0; i--) {
                ctx.lineTo(points[i].x, geo.yAt(Number(points[i].step.lb)));
            }
            ctx.closePath();
            ctx.fillStyle = COLORS.pastFill;
            ctx.fill();
        }

        ctx.strokeStyle = COLORS.pastLine;
        ctx.lineWidth = Math.max(1.2, 1.4 * geo.s);
        ctx.setLineDash([5, 4]);
        ctx.beginPath();
        ctx.moveTo(geo.xAt(active.index), geo.yAt(series.all[active.index].close));
        points.forEach(p => ctx.lineTo(p.x, geo.yAt(Number(p.step.c))));
        ctx.stroke();
        ctx.setLineDash([]);

        // Dot each forecast point, filled when the realized close landed inside
        // the band it predicted.
        points.forEach(p => {
            const inBand = p.step.in_band;
            ctx.beginPath();
            ctx.arc(p.x, geo.yAt(Number(p.step.c)), Math.max(1.6, 2 * geo.s), 0, Math.PI * 2);
            ctx.fillStyle = inBand === false ? COLORS.down : COLORS.past;
            ctx.globalAlpha = inBand === undefined ? 0.55 : 0.95;
            ctx.fill();
            ctx.globalAlpha = 1;
        });

        const baseX = geo.xAt(active.index);
        ctx.strokeStyle = 'rgba(122, 162, 255, 0.5)';
        ctx.lineWidth = 1;
        ctx.setLineDash([3, 4]);
        ctx.beginPath();
        ctx.moveTo(baseX, geo.pad.top);
        ctx.lineTo(baseX, geo.pad.top + geo.plotH);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.restore();
    }

    function snapshotTooltipHTML(snapshot, view) {
        const steps = snapshot.steps || [];
        if (!steps.length) return '';
        const picks = [];
        const wanted = [1, Math.min(steps.length, Math.max(2, Math.round(steps.length / 2))), steps.length];
        wanted.forEach(n => {
            if (n >= 1 && n <= steps.length && picks.indexOf(n) === -1) picks.push(n);
        });
        const unit = view.barMs >= 86400000 ? 'd' : 'h';
        let rows = '';
        picks.forEach(n => {
            const step = steps[n - 1];
            const predicted = formatPrice(Number(step.c));
            let detail = '<span class="tip-muted">pending</span>';
            if (Number.isFinite(Number(step.actual))) {
                const err = Number(step.error_pct);
                const cls = Math.abs(err) <= 1 ? 'tip-good' : (Math.abs(err) <= 3 ? '' : 'tip-bad');
                detail = formatPrice(Number(step.actual)) +
                    ' <span class="' + cls + '">' + (err >= 0 ? '+' : '') + err.toFixed(2) + '%</span>';
            }
            rows += '<tr><td>+' + n + unit + '</td><td>' + predicted + '</td><td>' + detail + '</td></tr>';
        });
        const label = snapshot.source === 'backfill' ? 'Reconstructed forecast' : 'Forecast made here';
        let footer = '';
        if (Number.isFinite(Number(snapshot.mae_pct))) {
            footer = 'MAE ' + Number(snapshot.mae_pct).toFixed(2) + '%';
            if (Number.isFinite(Number(snapshot.in_band_pct))) {
                footer += ' · ' + Math.round(Number(snapshot.in_band_pct)) + '% in band';
            }
            footer += ' <span class="tip-muted">(' + snapshot.scored + ' scored)</span>';
        } else {
            footer = '<span class="tip-muted">not yet scored</span>';
        }
        return '<div class="tip-forecast">' +
            '<div class="tip-head">' + label + '</div>' +
            '<table><thead><tr><th></th><th>Forecast</th><th>Actual</th></tr></thead><tbody>' + rows + '</tbody></table>' +
            '<div class="tip-foot">' + footer + '</div>' +
            '<div class="tip-muted">' + (state.pinnedIndex >= 0 ? 'Click to unpin' : 'Click to pin') + '</div>' +
            '</div>';
    }

    function historyCacheKey(view) {
        return view.interval + ':' + view.limit + ':' + view.steps;
    }

    function loadForecastHistory(view) {
        const key = historyCacheKey(view);
        const cached = state.historyCache[key];
        if (cached && Date.now() - cached.at < CACHE_TTL) {
            applyHistory(view, cached.data);
            return;
        }
        const token = ++state.historyToken;
        const url = '/api/homepage/forecast-history/BTC_USDT?interval=' + view.interval +
            '&bars=' + view.limit + '&steps=' + view.steps + '&limit=' + Math.min(200, view.limit);
        fetch(url)
            .then(response => (response.ok ? response.json() : null))
            .then(data => {
                if (!data || token !== state.historyToken) return;
                state.historyCache[key] = { at: Date.now(), data: data };
                applyHistory(view, data);
            })
            .catch(() => { /* hover replay is optional */ });
    }

    function applyHistory(view, data) {
        if (!state.view || state.view.key !== view.key) return;
        const byTs = {};
        const snapshots = Array.isArray(data.snapshots) ? data.snapshots : [];
        snapshots.forEach(snapshot => {
            byTs[normalizeTs(Number(snapshot.base_timestamp))] = snapshot;
        });
        state.history = { byTs: byTs, count: snapshots.length, accuracy: data.accuracy || {} };
        state.hoverIndex = -1;
        state.pinnedIndex = -1;
        updateAccuracyCaption(data);
        requestRedraw();
    }

    function updateAccuracyCaption(data) {
        const el = document.getElementById('hp-forecast-accuracy');
        if (!el) return;
        const accuracy = (data && data.accuracy) || {};
        const count = data && data.count ? data.count : 0;
        if (!count || !Number.isFinite(Number(accuracy.mae_pct))) {
            el.textContent = '';
            el.hidden = true;
            return;
        }
        let text = 'Past forecasts: ' + count + ' saved · MAE ' + Number(accuracy.mae_pct).toFixed(2) + '%';
        if (Number.isFinite(Number(accuracy.in_band_pct))) {
            text += ' · ' + Math.round(Number(accuracy.in_band_pct)) + '% inside band';
        }
        text += ' — hover a past candle to replay it';
        el.textContent = text;
        el.hidden = false;
    }

    function drawHorizonMarker(ctx, series, view, geo) {
        const target = series.predicted[Math.min(view.horizon, series.predicted.length) - 1];
        const last = series.history[series.history.length - 1];
        if (!target || !last || !last.close) return;
        const index = series.history.length + Math.min(view.horizon, series.predicted.length) - 1;
        const x = geo.xAt(index);
        const y = geo.yAt(target.close);
        const pct = ((target.close - last.close) / last.close) * 100;
        const label = view.horizonLabel + '  ' + formatPrice(target.close) + '  ' + (pct >= 0 ? '+' : '') + pct.toFixed(2) + '%';

        ctx.strokeStyle = 'rgba(245, 191, 61, 0.55)';
        ctx.lineWidth = 1.2;
        ctx.setLineDash([5, 4]);
        ctx.beginPath();
        ctx.moveTo(x, geo.pad.top);
        ctx.lineTo(x, geo.pad.top + geo.plotH);
        ctx.stroke();
        ctx.setLineDash([]);

        ctx.fillStyle = COLORS.gold;
        ctx.beginPath();
        ctx.arc(x, y, Math.max(2.5, 3 * geo.s), 0, Math.PI * 2);
        ctx.fill();

        ctx.font = '700 ' + Math.round(11 * geo.s) + 'px system-ui, sans-serif';
        const boxW = ctx.measureText(label).width + 18;
        const boxH = Math.round(22 * geo.s);
        const boxX = Math.min(geo.width - geo.pad.right - boxW - 4, Math.max(geo.pad.left + 4, x - boxW / 2));
        const boxY = Math.max(geo.pad.top + 4, y - boxH - 12 * geo.s);
        ctx.fillStyle = COLORS.labelBg;
        ctx.strokeStyle = 'rgba(245, 191, 61, 0.42)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.roundRect(boxX, boxY, boxW, boxH, [6]);
        ctx.fill();
        ctx.stroke();
        ctx.fillStyle = pct >= 0 ? COLORS.up : COLORS.down;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(label, boxX + boxW / 2, boxY + boxH / 2);
    }

    function ensureChart(container) {
        if (state.chart && state.chart.container === container && container.contains(state.chart.canvas)) {
            return state.chart;
        }
        const chart = createCanvas(container);
        state.chart = chart;
        const redraw = requestRedraw;
        if ('ResizeObserver' in window) {
            new ResizeObserver(redraw).observe(chart.canvas);
        } else {
            window.addEventListener('resize', redraw);
        }
        return chart;
    }

    function apply(view, data) {
        const container = document.getElementById('btc-chart-container');
        if (!container) return;
        const candles = data && Array.isArray(data.candles) ? data.candles : [];
        const forecast = data && Array.isArray(data.forecast) ? data.forecast : [];
        const series = buildSeries(candles, forecast, view);
        if (!series.history.length) {
            if (!state.series) container.innerHTML = '<p class="btc-chart-message">No data available</p>';
            showError('No data for ' + view.key);
            return;
        }
        state.series = series;
        state.data = data;
        const chart = ensureChart(container);
        drawChart(chart.canvas, chart.tooltip, series);
        updateHeadlineMetrics(series, data, view);
        loadForecastHistory(view);
    }

    function showError(message) {
        const container = document.getElementById('btc-chart-container');
        if (!container || !container.querySelector('.btc-chart-canvas-wrap')) return;
        let note = container.querySelector('.btc-chart-error');
        if (!note) {
            note = document.createElement('div');
            note.className = 'btc-chart-error';
            container.querySelector('.btc-chart-canvas-wrap').appendChild(note);
        }
        note.textContent = message;
        clearTimeout(state.errorTimer);
        state.errorTimer = setTimeout(() => {
            if (note.parentNode) note.parentNode.removeChild(note);
        }, 4000);
    }

    function updateHeadlineMetrics(series, data, view) {
        const history = series.history;
        const last = history[history.length - 1];
        const priceEl = document.getElementById('hp-btc-price');
        if (priceEl && last) priceEl.textContent = '$' + last.close.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        const changeEl = document.getElementById('hp-btc-change');
        if (changeEl && last) {
            const lookback = Math.max(1, Math.round(86400000 / view.barMs));
            const prior = history.length > lookback ? history[history.length - 1 - lookback] : history[0];
            const pct = prior && prior.close ? ((last.close - prior.close) / prior.close) * 100 : null;
            if (pct != null && Number.isFinite(pct)) {
                changeEl.textContent = (pct >= 0 ? '+' : '') + pct.toFixed(2) + '%';
                changeEl.classList.toggle('down', pct < 0);
                changeEl.classList.toggle('up', pct >= 0);
            }
        }
        const horizonEl = document.getElementById('hp-chart-horizon');
        if (horizonEl) horizonEl.textContent = view.title;
        const engineEl = document.getElementById('hp-engine-badge');
        if (engineEl && data) {
            const engine = String(data.forecast_engine || '');
            const label = engineEl.querySelector('.bb-engine-label');
            if (label) label.textContent = engine.indexOf('chronos2') === 0 ? 'Chronos2 AI' : 'AI Ensemble';
        }
    }

    function sampleData(view) {
        const now = Date.now();
        const bar = view.barMs;
        const candles = [];
        let price = 66000;
        const historyCount = Math.min(view.limit, 96);
        for (let i = historyCount; i > 0; i--) {
            const wave = Math.sin(i / 5) * 420 + Math.cos(i / 11) * 520;
            const drift = (historyCount - i) * 16;
            const close = price + wave + drift + Math.sin(i) * 130;
            candles.push({
                timestamp: now - i * bar,
                open: close - Math.sin(i / 2) * 180,
                high: close + 420 + Math.abs(Math.cos(i)) * 150,
                low: close - 460 - Math.abs(Math.sin(i)) * 130,
                close: close,
            });
        }
        const forecast = [];
        const last = candles[candles.length - 1].close;
        for (let i = 1; i <= view.steps; i++) {
            const close = last + i * 18 + Math.sin(i / 3) * 220;
            const band = 700 + i * 34;
            forecast.push({
                timestamp: now + i * bar,
                open: i === 1 ? last : forecast[i - 2].close,
                high: close + 260,
                low: close - 260,
                close: close,
                high_band: close + band,
                low_band: close - band,
                is_forecast: true,
            });
        }
        return { candles, forecast };
    }

    function setTabsDisabled(disabled) {
        const tabs = document.querySelectorAll('#hp-chart-tabs button');
        for (const tab of tabs) tab.disabled = disabled;
    }

    function loadView(view, options) {
        const opts = options || {};
        const container = document.getElementById('btc-chart-container');
        if (!container) return;
        if (!state.view || state.view.key !== view.key) {
            // Stored forecasts are keyed by bar timestamp; drop the old
            // interval's set rather than matching it against new bars.
            state.history = null;
            state.hoverIndex = -1;
            state.pinnedIndex = -1;
        }
        state.view = view;

        const cached = state.cache[view.key];
        if (cached && !opts.force && Date.now() - cached.at < CACHE_TTL) {
            apply(view, cached.data);
            return;
        }
        if (!state.series) {
            container.innerHTML = '<p class="btc-chart-loading">Loading chart...</p>';
            state.chart = null;
        } else {
            container.classList.add('is-loading');
        }
        setTabsDisabled(true);
        const token = ++state.token;
        const url = '/api/homepage/forecast/BTC_USDT?steps=' + view.steps +
            '&interval=' + view.interval + '&limit=' + view.limit + '&_=' + Date.now();
        fetch(url, { cache: 'no-store' })
            .then(response => {
                if (!response.ok) throw new Error('API error: ' + response.status);
                return response.json();
            })
            .then(data => {
                if (token !== state.token) return;
                state.cache[view.key] = { at: Date.now(), data: data };
                container.classList.remove('is-loading');
                setTabsDisabled(false);
                apply(view, data);
            })
            .catch(err => {
                if (token !== state.token) return;
                console.error('BTC chart error:', err);
                container.classList.remove('is-loading');
                setTabsDisabled(false);
                if (!state.series) {
                    apply(view, sampleData(view));
                } else {
                    showError('Could not load ' + view.key + ' data');
                }
            });
    }

    function selectView(key, options) {
        const view = VIEWS[key];
        if (!view) return;
        const tabs = document.querySelectorAll('#hp-chart-tabs button');
        for (const tab of tabs) {
            const active = tab.getAttribute('data-view') === key;
            tab.classList.toggle('active', active);
            tab.setAttribute('aria-selected', active ? 'true' : 'false');
        }
        loadView(view, options);
    }

    function bindTabs() {
        const tabs = document.getElementById('hp-chart-tabs');
        if (!tabs || tabs.dataset.bound) return;
        tabs.dataset.bound = '1';
        tabs.addEventListener('click', event => {
            const button = event.target.closest('button[data-view]');
            if (!button || button.disabled) return;
            const key = button.getAttribute('data-view');
            if (state.started && state.view && state.view.key === key) return;
            state.started = true;
            selectView(key);
        });
    }

    function startChart() {
        state.started = true;
        selectView(state.view.key);
    }

    function scheduleHomepageBTCChart() {
        const container = document.getElementById('btc-chart-container');
        if (!container) return;
        bindTabs();
        const activeTab = document.querySelector('#hp-chart-tabs button.active');
        if (activeTab && VIEWS[activeTab.getAttribute('data-view')]) {
            state.view = VIEWS[activeTab.getAttribute('data-view')];
        }
        if ('IntersectionObserver' in window) {
            const observer = new IntersectionObserver(entries => {
                if (entries.some(entry => entry.isIntersecting)) {
                    observer.disconnect();
                    startChart();
                }
            }, { rootMargin: '120px 0px' });
            observer.observe(container);
            return;
        }
        if ('requestIdleCallback' in window) {
            requestIdleCallback(startChart, { timeout: 1500 });
        } else {
            setTimeout(startChart, 500);
        }
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', scheduleHomepageBTCChart);
    } else {
        scheduleHomepageBTCChart();
    }

    window.refreshHomepageBTCChart = () => loadView(state.view, { force: true });
    setInterval(() => {
        if (state.started) loadView(state.view, { force: true });
    }, 60 * 60 * 1000);
})();
